// src/pages/indicadores.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { apiConfig } from "../config/api.config";

const COLORS = {
  info: "#3b82f6",
  purple: "#8b5cf6",
  danger: "#ef4444",
  gray: "#6b7280",
  green: "#10b981",
};

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatMoneyCO(v) {
  const n = toNum(v);
  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
}

function parseIndicadorCode(code) {
  const s = String(code || "").trim();
  if (!s) return { a: 99, b: 99, c: 99, d: 99, raw: "" };
  const parts = s.split(".").map((x) => parseInt(x, 10));
  return {
    a: Number.isFinite(parts[0]) ? parts[0] : 99,
    b: Number.isFinite(parts[1]) ? parts[1] : 99,
    c: Number.isFinite(parts[2]) ? parts[2] : 99,
    d: Number.isFinite(parts[3]) ? parts[3] : 99,
    raw: s,
  };
}

function compareIndicador(a, b) {
  const pa = parseIndicadorCode(a);
  const pb = parseIndicadorCode(b);
  if (pa.a !== pb.a) return pa.a - pb.a;
  if (pa.b !== pb.b) return pa.b - pb.b;
  if (pa.c !== pb.c) return pa.c - pb.c;
  if (pa.d !== pb.d) return pa.d - pb.d;
  return pa.raw.localeCompare(pb.raw);
}

// =====================================================
// MAPEO MANUAL — clave exacta = nombre de la secretaría
// =====================================================
const SECRETARIAS_MAP = {
  "Secretaría de Salud": new Set([
    "1.4.1.1",
    "1.4.2.1","1.4.2.2","1.4.2.3","1.4.2.4",
  ]),
  "Instituto de Deportes y Recreación de Caldas – Indec": new Set([
    "1.5.1.1","1.5.1.2","1.5.1.3",
    "1.5.2.1","1.5.2.2",
    "1.5.4.1","1.5.4.2","1.5.4.3","1.5.4.4",
  ]),
  "Secretaría de Infraestructura": new Set([
    "1.6.1.1","1.6.1.2","1.6.1.3","1.6.1.4","1.6.1.5",
    "1.6.3.1","1.6.3.2","1.6.3.3","1.6.3.4","1.6.3.5",
    "1.6.3.6","1.6.3.7","1.6.3.8","1.6.3.9",
    "1.7.1.1","1.7.1.2","1.7.1.3","1.7.1.4","1.7.1.5",
    "1.7.1.6","1.7.1.7","1.7.1.8","1.7.1.9","1.7.1.10",
    "1.7.3.1","1.7.3.2","1.7.3.3",
    "2.3.3.1","2.3.3.2","2.3.3.3",
    "2.4.1.1","2.4.1.2","2.4.1.3","2.4.1.4","2.4.1.5",
    "2.4.3.1","2.4.4.1","2.4.4.2",
    "2.4.5.1","2.4.5.2","2.4.5.3","2.4.5.4","2.4.5.5","2.4.5.6",
    "3.1.2.1",
    "4.1.1.1",
    "4.1.2.1","4.1.2.2","4.1.2.3","4.1.2.4","4.1.2.5","4.1.2.6",
  ]),
  "Secretaría de Planeación": new Set([
    "3.1.3.1","3.1.3.2","3.1.5.1","3.1.6.1","3.1.6.2","3.1.6.3",
    "3.2.1.1",
    "3.2.2.1","3.2.2.2","3.2.2.3",
    "3.2.3.1","3.2.3.2","3.2.3.3","3.2.3.4",
    "3.3.1.1","3.3.1.2","3.3.1.3","3.3.1.4",
    "3.3.2.1","3.3.2.2","3.3.2.3","3.3.2.4",
    "3.3.4.1","3.3.5.1","3.3.5.2",
    "4.1.2.7","4.1.2.8",
    "4.2.2.1","4.2.3.1","4.2.3.2","4.2.3.3","4.2.4.1",
  ]),
  "Secretaría de Desarrollo Económico y Social": new Set([
    "2.1.1.1","2.1.1.2","2.1.1.3",
    "2.1.3.1",
    "2.1.5.1","2.1.5.2","2.1.5.3","2.1.5.4","2.1.5.5",
    "2.1.5.6","2.1.5.7","2.1.5.8","2.1.5.9","2.1.5.10",
    "2.2.2.1",
    "2.2.3.1","2.2.3.2","2.2.3.3","2.2.3.4",
    "2.3.1.1","2.3.1.2","2.3.1.3",
    "2.3.2.1","2.3.2.2","2.3.2.3",
    "2.3.4.1",
  ]),
  "Secretaría de Mujer y Familia": new Set([
    "1.8.1.1","1.8.1.2",
    "1.8.2.1","1.8.2.2",
    "1.8.3.1","1.8.3.2","1.8.3.3",
    "1.8.4.1",
    "1.8.5.1","1.8.5.2","1.8.5.3",
    "1.8.7.1","1.8.7.2","1.8.7.3",
    "1.9.1.1",
    "1.9.3.1","1.9.3.2",
    "1.9.4.1","1.9.4.2","1.9.4.3","1.9.4.4","1.9.4.5","1.9.4.6",
    "1.10.2.1",
    "1.10.3.1","1.10.3.2","1.10.3.3","1.10.3.4","1.10.3.5",
    "1.10.4.1","1.10.4.2","1.10.4.3","1.10.4.4",
    "1.11.2.1","1.11.2.2",
    "1.11.4.1",
    "1.11.5.1","1.11.5.2",
    "1.12.1.1","1.12.1.2","1.12.1.3","1.12.1.4","1.12.1.5",
    "1.12.1.6","1.12.1.7","1.12.1.8","1.12.1.9","1.12.1.10",
    "1.12.2.1",
    "1.13.1.1","1.13.1.2","1.13.1.3",
    "1.13.2.1",
    "1.13.3.1","1.13.3.2","1.13.3.3",
    "1.13.4.1","1.13.5.1",
    "1.14.1.1","1.14.1.2","1.14.1.3","1.14.1.4",
    "1.14.3.1","1.14.3.2",
    "1.14.4.1","1.14.4.2","1.14.4.3","1.14.4.4",
    "1.15.2.1",
    "1.15.3.1","1.15.3.2","1.15.3.3",
  ]),
  "Secretaría de Seguridad y Convivencia": new Set([
    "4.3.1.1","4.3.1.2","4.3.1.3","4.3.1.4","4.3.1.5",
    "4.3.2.1","4.3.2.2","4.3.2.3","4.3.2.4",
    "4.3.3.1","4.3.3.2",
    "4.3.4.1",
    "4.4.1.1","4.4.2.1","4.4.2.2","4.4.2.3","4.4.2.4",
  ]),
  "Oficina de Comunicaciones y TI": new Set([
    "1.7.4.1",
    "5.1.1.1","5.1.1.2","5.1.1.3","5.1.1.4","5.1.1.5",
    "5.1.2.1","5.1.2.2",
  ]),
  "Secretaría de Hacienda": new Set([
    "4.5.1.1","4.5.1.2",
    "4.5.3.1","4.5.3.2","4.5.3.3",
    "4.6.2.1",
    "4.6.3.1","4.6.3.2",
  ]),
  "Alcaldía": new Set([
    "2.3.4.2","2.3.4.3","2.3.4.4",
  ]),
  "Secretaría de Educación": new Set([
    // Sin indicadores específicos en el plan de Caldas cargado
  ]),
  "Secretaría de Servicios Administrativos": new Set([
    // Sin indicadores específicos en el plan de Caldas cargado
  ]),
  "Secretaría de Movilidad": new Set([
    // Sin indicadores específicos en el plan de Caldas cargado
  ]),
};

// =====================================================
// NORMALIZACIÓN: valor del API → clave de SECRETARIAS_MAP
// =====================================================
const NORMALIZE_MAP = {
  // Nombres completos con tilde
  "secretaría de salud":                                    "Secretaría de Salud",
  "secretaría de infraestructura":                          "Secretaría de Infraestructura",
  "secretaría de planeación":                               "Secretaría de Planeación",
  "secretaría de desarrollo económico y social":            "Secretaría de Desarrollo Económico y Social",
  "secretaría de mujer y familia":                          "Secretaría de Mujer y Familia",
  "secretaría de seguridad y convivencia":                  "Secretaría de Seguridad y Convivencia",
  "oficina de comunicaciones y ti":                         "Oficina de Comunicaciones y TI",
  "instituto de deportes y recreación de caldas – indec":   "Instituto de Deportes y Recreación de Caldas – Indec",
  "instituto de deportes y recreacion de caldas - indec":   "Instituto de Deportes y Recreación de Caldas – Indec",
  "indec":                                                  "Instituto de Deportes y Recreación de Caldas – Indec",
  "secretaría de hacienda":                                 "Secretaría de Hacienda",
  "alcaldía":                                               "Alcaldía",
  "alcaldia":                                               "Alcaldía",
  "secretaría de educación":                                "Secretaría de Educación",
  "secretaría de servicios administrativos":                "Secretaría de Servicios Administrativos",
  "secretaría de movilidad":                                "Secretaría de Movilidad",
  // Sin tilde
  "secretaria de salud":                                    "Secretaría de Salud",
  "secretaria de infraestructura":                          "Secretaría de Infraestructura",
  "secretaria de planeacion":                               "Secretaría de Planeación",
  "secretaria de desarrollo economico y social":            "Secretaría de Desarrollo Económico y Social",
  "secretaria de mujer y familia":                          "Secretaría de Mujer y Familia",
  "secretaria de seguridad y convivencia":                  "Secretaría de Seguridad y Convivencia",
  "oficina de comunicaciones":                              "Oficina de Comunicaciones y TI",
  "secretaria de hacienda":                                 "Secretaría de Hacienda",
  "secretaria de educacion":                                "Secretaría de Educación",
  "secretaria de servicios administrativos":                "Secretaría de Servicios Administrativos",
  "secretaria de movilidad":                                "Secretaría de Movilidad",
  // Abreviaciones
  "salud":            "Secretaría de Salud",
  "infraestructura":  "Secretaría de Infraestructura",
  "planeacion":       "Secretaría de Planeación",
  "planeación":       "Secretaría de Planeación",
  "desarrollo":       "Secretaría de Desarrollo Económico y Social",
  "mujer":            "Secretaría de Mujer y Familia",
  "seguridad":        "Secretaría de Seguridad y Convivencia",
  "comunicaciones":   "Oficina de Comunicaciones y TI",
  "hacienda":         "Secretaría de Hacienda",
  "educacion":        "Secretaría de Educación",
  "educación":        "Secretaría de Educación",
  "servicios":        "Secretaría de Servicios Administrativos",
  "servicios administrativos": "Secretaría de Servicios Administrativos",
  "movilidad":        "Secretaría de Movilidad",
  // Códigos
  "01001": "Secretaría de Infraestructura",
  "02001": "Secretaría de Planeación",
  "03001": "Secretaría de Desarrollo Económico y Social",
  "04001": "Secretaría de Mujer y Familia",
  "05001": "Secretaría de Seguridad y Convivencia",
  "05002": "Secretaría de Salud",
  "06001": "Oficina de Comunicaciones y TI",
  "06002": "Instituto de Deportes y Recreación de Caldas – Indec",
  "06003": "Secretaría de Hacienda",
  "06006": "Alcaldía",
  "06007": "Secretaría de Educación",
  "06008": "Secretaría de Servicios Administrativos",
  "06009": "Secretaría de Movilidad",
};

function normalizeSecretaria(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (SECRETARIAS_MAP[trimmed]) return trimmed;
  const lower = trimmed.toLowerCase();
  if (NORMALIZE_MAP[lower]) return NORMALIZE_MAP[lower];
  const sinTildes = lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (NORMALIZE_MAP[sinTildes]) return NORMALIZE_MAP[sinTildes];
  return trimmed;
}

function secretariaFromLista(code) {
  const c = String(code || "").trim();
  if (!c) return "SIN_SECRETARIA";
  for (const [sec, set] of Object.entries(SECRETARIAS_MAP)) {
    if (set.has(c)) return sec;
  }
  return "SIN_SECRETARIA";
}

export default function Indicadores() {
  const [loading, setLoading] = useState(true);
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState("");
  const [filtroFecha, setFiltroFecha]           = useState("todo");
  const [filtroSecretaria, setFiltroSecretaria] = useState("todas");

  const cargar = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);
      const url = apiConfig.getUrl(
        "/api/indicadores.php",
        `filtro=${encodeURIComponent(filtroFecha)}&t=${Date.now()}`
      );
      const resp = await fetch(url, { credentials: "include" });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.ok) {
        throw new Error(json?.error || json?.message || `Error ${resp.status}`);
      }
      setData(json.data || null);
    } catch (e) {
      setData(null);
      setErr(e?.message || "No se pudo cargar Indicadores");
    } finally {
      setLoading(false);
    }
  }, [filtroFecha]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    const id = setInterval(() => cargar(), 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [cargar]);

  // ===== TABLA =====
  const tabla = useMemo(() => {
    const rows = Array.isArray(data?.rows) ? data.rows : [];

    const rowsNorm = rows.map((r) => ({
      ...r,
      secretaria: normalizeSecretaria(r?.secretaria) || secretariaFromLista(r?.indicador),
    }));

    const rowsFiltrados = filtroSecretaria === "todas"
      ? rowsNorm
      : rowsNorm.filter((r) => r.secretaria === filtroSecretaria);

    const agrupado = {};
    rowsFiltrados.forEach((r) => {
      const ind = String(r?.indicador ?? "").trim();
      if (!ind) return;
      const count = toNum(r?.contratos_aprobados ?? r?.count ?? 0);
      const pres  = toNum(r?.presupuesto_total ?? r?.presupuesto ?? 0);

      if (!agrupado[ind]) {
        agrupado[ind] = { indicador: ind, secretaria: r.secretaria, count: 0, presupuesto_total: 0 };
      }
      agrupado[ind].count            += count;
      agrupado[ind].presupuesto_total += pres;
    });

    if (filtroSecretaria !== "todas") {
      const codigosSecretaria = Array.from(SECRETARIAS_MAP[filtroSecretaria] || []);
      const resultado = codigosSecretaria.map((codigo) => {
        const ex = agrupado[codigo];
        return {
          indicador:         codigo,
          secretaria:        filtroSecretaria,
          count:             ex ? Math.round(ex.count) : 0,
          presupuesto_total: ex ? ex.presupuesto_total : 0,
        };
      });
      resultado.sort((a, b) => compareIndicador(a.indicador, b.indicador));
      return resultado;
    }

    const base = Object.values(agrupado);
    base.sort((a, b) => compareIndicador(a.indicador, b.indicador));
    return base;
  }, [data, filtroSecretaria]);

  // ===== KPIs =====
  const kpis = useMemo(() => {
    const calcFromTabla = () => ({
      total_aprobados:     Math.round(tabla.reduce((a, r) => a + toNum(r.count), 0)),
      valor_total:         tabla.reduce((a, r) => a + toNum(r.presupuesto_total), 0),
      con_indicador_count: tabla.filter((r) => toNum(r.count) > 0).length,
      con_indicador_valor: tabla.reduce(
        (a, r) => a + (toNum(r.presupuesto_total) > 0 ? toNum(r.presupuesto_total) : 0), 0
      ),
    });

    if (filtroSecretaria !== "todas") return calcFromTabla();

    const apiKpis = data?.kpis;
    if (apiKpis && typeof apiKpis === "object") {
      return {
        total_aprobados:     toNum(apiKpis.total_aprobados),
        valor_total:         toNum(apiKpis.presupuesto_total_aprobado),
        con_indicador_count: toNum(apiKpis.con_indicador_count),
        con_indicador_valor: toNum(apiKpis.con_indicador_valor),
      };
    }
    return calcFromTabla();
  }, [data, tabla, filtroSecretaria]);

  const secretariasDisponibles = useMemo(
    () => Object.keys(SECRETARIAS_MAP).sort((a, b) => a.localeCompare(b, "es")),
    []
  );

  if (loading) {
    return (
      <div style={{ display: "flex", minHeight: "60vh", alignItems: "center", justifyContent: "center" }}>
        <div style={{
          width: 48, height: 48,
          border: "4px solid #e5e7eb", borderTop: "4px solid #3b82f6",
          borderRadius: "50%", animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto", backgroundColor: "#f9fafb", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.5rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: "1.7rem", fontWeight: 900, color: "#111827", margin: 0 }}>
              📌 Plan de Desarrollo – Indicadores
            </h1>
            <p style={{ color: "#6b7280", margin: "0.35rem 0 0 0", fontSize: "1rem", fontWeight: 700 }}>
              Actualiza automáticamente cada 10 minutos.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <select value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)} style={selectStyle}>
              <option value="todo">Todo el tiempo</option>
              <option value="30dias">Últimos 30 días</option>
              <option value="90dias">Últimos 90 días</option>
              <option value="año">Último año</option>
            </select>
            <select value={filtroSecretaria} onChange={(e) => setFiltroSecretaria(e.target.value)} style={selectStyle}>
              <option value="todas">Todas las secretarías</option>
              {secretariasDisponibles.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              <option value="SIN_SECRETARIA">SIN_SECRETARIA</option>
            </select>
            <button onClick={cargar} style={btnStyle}>
              <RefreshCw size={18} />
              Refrescar
            </button>
          </div>
        </div>
        {err && <div style={{ marginTop: "0.9rem", color: COLORS.danger, fontWeight: 900 }}>{err}</div>}
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <Kpi title="Total contratos aprobados"  value={`${kpis.total_aprobados}`}                      color={COLORS.info}   />
        <Kpi title="Presupuesto aprobado total" value={`$ ${formatMoneyCO(kpis.valor_total)}`}         color={COLORS.green}  />
        <Kpi title="Indicadores con contratos"  value={`${kpis.con_indicador_count}`}                  color={COLORS.info}   />
        <Kpi title="Presupuesto con indicador"  value={`$ ${formatMoneyCO(kpis.con_indicador_valor)}`} color={COLORS.purple} />
      </div>

      {/* Tabla */}
      <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "#111827" }}>
            {filtroSecretaria === "todas" ? "Resumen por indicador" : `Indicadores – ${filtroSecretaria}`}
          </div>
          <div style={{ color: COLORS.gray, fontWeight: 800 }}>
            Indicadores: {tabla.length}
            {filtroSecretaria !== "todas" && (
              <span style={{ marginLeft: "0.75rem", color: COLORS.info }}>
                Con contratos: {tabla.filter((r) => r.count > 0).length}
              </span>
            )}
          </div>
        </div>

        {tabla.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280", fontWeight: 800 }}>
            <FileText size={42} style={{ marginBottom: "0.5rem" }} />
            <div>No hay datos para mostrar.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <Th>Código</Th>
                  {filtroSecretaria === "todas" && <Th>Secretaría</Th>}
                  <Th align="center">Contratos aprobados</Th>
                  <Th align="right">Presupuesto aprobado</Th>
                </tr>
              </thead>
              <tbody>
                {tabla.map((r, idx) => {
                  const sinContratos = r.count === 0;
                  const rowBg      = sinContratos ? "#fafafa" : idx % 2 === 0 ? "white" : "#f9fafb";
                  const colorTexto = sinContratos ? "#9ca3af" : "inherit";
                  return (
                    <tr key={`${r.indicador}-${r.secretaria}-${idx}`} style={{ backgroundColor: rowBg }}>
                      <Td strong style={{ color: colorTexto, fontFamily: "monospace", fontSize: "0.9rem" }}>
                        {r.indicador}
                      </Td>
                      {filtroSecretaria === "todas" && (
                        <Td style={{ color: colorTexto }}>{r.secretaria}</Td>
                      )}
                      <Td strong style={{ color: colorTexto, textAlign: "center" }}>
                        {Math.round(r.count)}
                      </Td>
                      <Td strong style={{ color: colorTexto, textAlign: "right" }}>
                        {r.presupuesto_total === 0 ? "$ 0" : `$ ${formatMoneyCO(r.presupuesto_total)}`}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const selectStyle = {
  padding: "0.6rem 1rem", border: "1px solid #d1d5db",
  borderRadius: "10px", fontSize: "1rem", cursor: "pointer", fontWeight: 800,
};

const btnStyle = {
  padding: "0.6rem 1rem", backgroundColor: "#8b5cf6", color: "white",
  border: "none", borderRadius: "10px", fontSize: "1rem", cursor: "pointer",
  display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 800,
};

function Kpi({ title, value, color }) {
  return (
    <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "1.25rem" }}>
      <div style={{ fontSize: "1rem", color: "#6b7280", fontWeight: 800 }}>{title}</div>
      <div style={{ fontSize: "2rem", fontWeight: 900, color, marginTop: "0.4rem" }}>{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{ textAlign: align, padding: "10px", borderBottom: "1px solid #e5e7eb", fontWeight: 900, whiteSpace: "nowrap", fontSize: "0.95rem" }}>
      {children}
    </th>
  );
}

function Td({ children, strong = false, style = {} }) {
  return (
    <td style={{ padding: "10px", borderBottom: "1px solid #e5e7eb", fontWeight: strong ? 900 : 800, whiteSpace: "nowrap", verticalAlign: "middle", ...style }}>
      {children}
    </td>
  );
}